import { QURAN_CREDIT, RASA, kemenagUrl } from '@iqrapath/shared'
import { ChevronRight, CircleUserRound, Mail, Moon, MoonStar, Sun, Sunrise, Sunset, type LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Obj } from '../../components/Obj.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { useSession } from '../../lib/queries.ts'
import { RASA_INFO } from '../pegangan/pegangan.ts'
import { isFriday } from '../perjalanan/perjalanan.ts'
import { useReading, useSurahs } from '../quran/quran.ts'
import { devLitOverride, useSky } from '../../lib/useSky.ts'
import { Island } from './Island.tsx'
import { SkyBackdrop } from './SkyBackdrop.tsx'
import { useToday, type AyatSummary, type Today } from './today.ts'

const SKY_ICON: Record<string, LucideIcon> = { sunrise: Sunrise, sun: Sun, sunset: Sunset, 'moon-star': MoonStar }

const dateFmt = new Intl.DateTimeFormat('id-ID', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Asia/Jakarta' })

function Stones({ lit, total = 30 }: { lit: number; total?: number }) {
  return (
    <div className="stones" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => <i key={i} className={i < lit ? 'on' : i === lit ? 'next' : ''} />)}
    </div>
  )
}

function AyatCard({ a, done }: { a: AyatSummary; done: boolean }) {
  return (
    <section className="card ayat-card reveal" aria-label="Ayat hari ini">
      <div className="meta">
        <span className="pill pill-mint">Ayat ke-{a.day}{a.tema ? ` · ${a.tema}` : ''}</span>
        <span className="num muted" style={{ fontSize: '.78rem' }}>5–10 menit</span>
      </div>
      {a.ar ? (
        <>
          <p className="ar" lang="ar" dir="rtl">{a.ar}</p>
          {a.terjemah && <p className="tr">“{a.terjemah}”</p>}
        </>
      ) : (
        <p className="tr" style={{ marginTop: 12 }}>Teks ayat akan tampil di sini setelah data Al-Qur’an terpasang.</p>
      )}
      <p className="src"><SourceLink href={kemenagUrl(a.surah, a.ayatFrom, a.ayatTo)}>{a.ref}</SourceLink></p>
      {a.ar && <p className="credit">{QURAN_CREDIT}</p>}
      {done ? (
        <>
          <p className="cta-sub" style={{ marginTop: 14 }}>Ayat hari ini sudah kamu jalani. Giliran ayat selanjutnya datang besok pagi.</p>
          <Link className="btn btn-ghost btn-block" to={`/ayat/${a.day}`}>Buka lagi ayat ini</Link>
        </>
      ) : (
        <>
          <Link className="btn btn-primary btn-block" to={`/ayat/${a.day}`}>Mulai ayat hari ini</Link>
          <p className="cta-sub">Baca, pahami, renungi, amalkan. Lampu taman menyala setelahnya.</p>
        </>
      )}
    </section>
  )
}

function NoAyat({ finishedAll }: { finishedAll: boolean }) {
  return (
    <section className="card ayat-card reveal" aria-label="Ayat hari ini">
      <Obj id={finishedAll ? 'lampu' : 'jam-pasir'} size={64} />
      <b style={{ display: 'block', marginTop: 10 }}>{finishedAll ? 'Semua ayat yang tersedia sudah kamu jalani' : 'Ayat hari ini sedang disiapkan'}</b>
      <p className="tr" style={{ marginTop: 4 }}>
        {finishedAll ? 'Masya Allah. Ayat berikutnya segera ditambahkan.' : 'Sambil menunggu, coba jelajahi Al-Qur’an per kata.'}
      </p>
      <Link className="btn btn-ghost btn-block" to="/quran">Buka Al-Qur’an per kata</Link>
    </section>
  )
}

/** "Lanjut baca": straight to the last verse read when there is one, else the Qur'an tab. */
function ContinueQuran() {
  const { data: reading } = useReading()
  const { data: surahs } = useSurahs()
  const last = reading && surahs?.find((s) => s.n === reading.surah)
  return last ? (
    <Link className="card row-card reveal" to={`/quran/${last.n}?ayat=${reading.ayah}`}>
      <span className="ic obj-ic" aria-hidden="true"><Obj id="mushaf" /></span>
      <div><b>Lanjut baca {last.nama} · ayat {reading.ayah}</b><small>Terakhir kamu baca di sini. Sentuh untuk melanjutkan.</small></div>
      <ChevronRight className="chev" size={18} aria-hidden="true" />
    </Link>
  ) : (
    <Link className="card row-card reveal" to="/quran">
      <span className="ic obj-ic" aria-hidden="true"><Obj id="mushaf" /></span>
      <div><b>Lanjut baca Al-Qur’an</b><small>114 surat, arti tiap kata, dan suara qari.</small></div>
      <ChevronRight className="chev" size={18} aria-hidden="true" />
    </Link>
  )
}

function status(t: Today | undefined, lit: boolean) {
  if (!t) return { t: 'Taman ngajimu', s: 'Sedang menyiapkan ayat hari ini.' }
  if (lit) return { t: 'Lampu hari ini sudah terang', s: t.stats.streak > 1 ? `${t.stats.streak} hari berturut-turut. Semoga berkah langkahmu.` : 'Semoga berkah langkahmu.' }
  if (!t.ayat) return { t: 'Taman ngajimu', s: t.finishedAll ? 'Semua ayat yang tersedia sudah kamu jalani.' : 'Ayat hari ini sedang disiapkan.' }
  return { t: 'Lampu taman masih padam', s: 'Satu ayat hari ini akan menyalakannya.' }
}

export function HomePage() {
  const sky = useSky()
  const { data: session } = useSession()
  const { data: today, isPending, isError, refetch } = useToday()
  const me = session?.state === 'ok' ? session.me : null
  const first = me?.user.name.trim().split(/\s+/)[0]
  const Icon = SKY_ICON[sky.icon] ?? Moon

  const lit = devLitOverride() ?? Boolean(today?.done)
  const st = status(today, lit)
  const s = today?.stats

  return (
    <>
      <header className="hero" data-tone={sky.phase === 'siang' ? 'light' : 'dark'}>
        <SkyBackdrop phase={sky.phase} />
        <div className="hero-top">
          <div>
            <small>{dateFmt.format(new Date())}</small>
            <h1>{first ? <>Assalamu’alaikum,<br />{first}</> : <>Assalamu’alaikum</>}</h1>
          </div>
          <Link className="icon-btn" to="/pengaturan" aria-label="Profil dan pengaturan"><CircleUserRound size={22} /></Link>
        </div>
        <div className="stage"><Island phase={sky.phase} lit={lit} /></div>
        <div className="hero-cap">
          <span className="sky-pill"><Icon size={16} aria-hidden="true" />{sky.label}</span>
          <p className="hero-status" role="status">{st.t}<small>{st.s}</small></p>
        </div>
      </header>

      <div className="sheet-top">
        <div className="sheet-in">
          {isPending && <SkCard label="Memuat ayat hari ini" ar lines={2} h={230} />}
          {isError && (
            <div className="card" style={{ padding: 18 }}>
              <b>Belum bisa memuat ayat hari ini</b>
              <p className="muted" style={{ marginTop: 4 }}>Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.</p>
              <button className="btn btn-secondary btn-block" style={{ marginTop: 12 }} onClick={() => refetch()}>Coba lagi</button>
            </div>
          )}
          {today && (
            <>
              {today.ayat ? <AyatCard a={today.ayat} done={today.done} /> : <NoAyat finishedAll={today.finishedAll} />}

              {isFriday(today.today) && (
                <Link className="card row-card home-jumat reveal" to="/perjalanan/jumat">
                  <span className="ic amber" aria-hidden="true"><Mail size={20} /></span>
                  <div><b>Kabar Jumat sudah datang</b>{' '}<small>Tengok sepekan perjalananmu, dan bekal untuk pekan depan.</small></div>
                  <ChevronRight className="chev" size={18} aria-hidden="true" />
                </Link>
              )}

              <section className="card pijakan reveal" aria-label="Pijakan">
                <div className="head">
                  <div className="big num">{s!.streak}<small>hari berturut</small></div>
                  <span className="pill pill-amber">Etape ke-{s!.etape}</span>
                </div>
                <Stones lit={s!.stones} />
                <p className="note">
                  {s!.streak === 0 && s!.bestStreak > 0
                    ? `Rekor terpanjangmu ${s!.bestStreak} hari. Jalan ini tidak ke mana-mana, ayo mulai lagi.`
                    : `Satu batu pijakan untuk tiap ayat yang kamu selesaikan. ${Math.max(0, 30 - s!.stones)} batu lagi untuk melengkapi satu etape.`}
                </p>
              </section>

              <div className="stats">
                <div className="card stat reveal"><b className="num">{s!.totalWords.toLocaleString('id-ID')}</b><span>kata sudah kamu pahami</span></div>
                <div className="card stat reveal"><b className="num">{s!.totalAyat.toLocaleString('id-ID')}</b><span>ayat sudah kamu jalani</span></div>
              </div>
            </>
          )}

          <div className="sec-h reveal"><h2>Lagi merasakan apa?</h2><Link to="/pegangan">Semua kartu</Link></div>
          <p className="muted" style={{ fontSize: '.88rem', marginBottom: 8 }}>Pilih yang paling dekat. Ada ayat yang bisa jadi pegangan, dan bisa kamu kirim ke orang yang sedang butuh.</p>
          <nav className="rasa reveal" aria-label="Pilih perasaan">
            {RASA.map((r) => <Link key={r} to={`/pegangan?rasa=${r}`}><Obj id={RASA_INFO[r].obj} size={30} />{RASA_INFO[r].label}</Link>)}
          </nav>

          <div className="stack" style={{ marginTop: 14 }}>
            <ContinueQuran />
          </div>

          <p className="help" style={{ textAlign: 'center', marginTop: 22 }}>Renungan di IqraPath adalah bahan renungan, bukan fatwa.</p>
        </div>
      </div>
    </>
  )
}
